<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Organization</div>
        <h3 class="duallo-table-title">Marker Categories</h3>
      </div>
      <div class="table-controls">
        <v-switch v-model="showInactive" label="Show deleted" color="primary" hide-details inset density="compact" />
        <DisplayOverlayWindowButton ref="newCategoryWindow" buttonText="Add Category" iconName="mdi-plus"
          windowTitle="Add Category">
          <AddMarkerCategoryComponent @onCategoryCreated="categoryCreated" @onCloseWindow="closeNewCategoryWindow" />
        </DisplayOverlayWindowButton>
      </div>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="filteredCategories.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-shape-outline</v-icon>
        <p class="empty-title">No marker categories</p>
        <p class="empty-subtitle">Add a custom category to start tagging anomalies.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-name">Name</th>
                <th class="col-desc">Description</th>
                <th class="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <MarkerCategoryTableItem v-for="category in filteredCategories" :key="category.id" :category="category"
                :compact="false" @onCategoryModified="categoryModified" />
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <MarkerCategoryTableItem v-for="category in filteredCategories" :key="category.id" :category="category"
            :compact="true" @onCategoryModified="categoryModified" />
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useDisplay } from 'vuetify';
import MarkerService from '@/services/MarkerService';
import { useOrganizationStore } from '@/store/organizationStore'
import DisplayOverlayWindowButton from '../buttons/DisplayOverlayWindowButton.vue';
import AddMarkerCategoryComponent from '../AddMarkerCategoryComponent.vue';
import MarkerCategoryTableItem from './table-items/MarkerCategoryTableItem.vue';

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const markerTypes = ref([]);
const organizationStore = useOrganizationStore();
const isLoading = ref(true);
const showInactive = ref(false);
const newCategoryWindow = ref(null);

const filteredCategories = computed(() =>
  markerTypes.value.filter(c => showInactive.value ? true : c.isActive)
);

function closeNewCategoryWindow() {
  newCategoryWindow.value?.closeWindow();
}

const categoryCreated = async () => {
  await loadCategories();
  closeNewCategoryWindow();
}

const categoryModified = async () => {
  await loadCategories();
}

const loadCategories = async () => {
  isLoading.value = true;
  try {
    markerTypes.value = await MarkerService.getMarkerTypesByOrganizationId(organizationStore.organizationId);
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadCategories);
</script>

<style scoped>
.table-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.table-controls :deep(.v-label) {
  color: #cfd8dc;
  font-size: 0.85rem;
}

.empty-state {
  align-items: center;
  text-align: center;
  padding: 40px 16px;
  color: #8aa0aa;
}

.empty-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #cfd8dc;
  margin: 12px 0 4px;
}

.empty-subtitle {
  font-size: 0.88rem;
  margin: 0;
  max-width: 380px;
}

.col-name { min-width: 140px; }
.col-actions { width: 120px; text-align: right; }
</style>
